<template>
  <div>
    <!-- Title -->
    <h4 class="my-2">
      <strong>{{ $t('forms.domain.hardware.title') }}</strong>
    </h4>
    <!-- Domain changed data -->
    <p
      v-if="domain.limitedHardware"
      class="text-danger font-weight-bold"
    >
      <b-icon
        class="mr-2"
        variant="danger"
        icon="exclamation-triangle-fill"
      />
      {{ $t('forms.domain.hardware.warning') }}
      <b-row>
        <b-col
          v-for="[key, value] of Object.entries(domain.limitedHardware)"
          :key="key"
          cols="12"
          xl="2"
        >
          <ul>
            {{ hardwareWarningTitle[key] }}
            <li v-if="['videos', 'boot_order', 'isos', 'vgpus', 'floppies', 'interfaces'].includes(key)">
              <span
                v-for="change of value['old_value']"
                :key="change['id']"
              >
                {{ change['name'] }}
              </span>
              <b-icon
                v-if="Array.isArray(value['new_value']) && value['new_value'].length > 0"
                icon="arrow-right"
              />
              <span
                v-for="change of value['new_value']"
                :key="change['id']"
              >
                {{ change['name'] }}
              </span>
            </li>
            <li v-else>
              <span>
                {{ value['old_value'] }}
              </span>
              <b-icon
                icon="arrow-right"
              />
              <span>
                {{ value['new_value'] }}
              </span>
            </li>
          </ul>
        </b-col>
      </b-row>
    </p>
    <!-- vCPUS -->
    <b-row>
      <b-col
        cols="12"
        xl="2"
        class="mb-2"
      >
        {{ $t(`forms.domain.hardware.vcpus`) }}
        <v-select
          v-model="vcpus"
          :options="availableHardware.vcpus"
          label="name"
          @search:blur="v$.vcpus.$touch"
        >
          <template #search="{ attributes, events }">
            <input
              id="vcpus"
              class="vs__search"
              v-bind="attributes"
              v-on="events"
            >
          </template>
        </v-select>
        <div
          v-if="v$.vcpus.$error"
          id="vcpusError"
          class="text-danger"
        >
          {{ $t(`validations.${v$.vcpus.$errors[0].$validator}`, { property: `${$t("forms.domain.hardware.vcpus")}` }) }}
        </div>
      </b-col>
      <!-- Memory -->
      <b-col
        cols="12"
        xl="2"
        class="mb-2"
      >
        {{ $t(`forms.domain.hardware.memory`) }}
        <v-select
          v-model="memory"
          :options="availableHardware.memory"
          label="name"
          @search:blur="v$.memory.$touch"
        >
          <template #search="{ attributes, events }">
            <input
              id="memory"
              class="vs__search"
              v-bind="attributes"
              v-on="events"
            >
          </template>
        </v-select>
        <div
          v-if="v$.memory.$error"
          id="memoryError"
          class="text-danger"
        >
          {{ $t(`validations.${v$.memory.$errors[0].$validator}`, { property: `${$t("forms.domain.hardware.memory")}` }) }}
        </div>
      </b-col>
      <!-- Videos -->
      <b-col
        cols="12"
        xl="2"
        class="mb-2"
      >
        {{ $t(`forms.domain.hardware.videos`) }}
        <v-select
          v-model="videos"
          :options="availableHardware.videos"
          label="name"
          :reduce="element => element.id"
          @search:blur="v$.videos.$touch"
        >
          <template #search="{ attributes, events }">
            <input
              id="videos"
              class="vs__search"
              v-bind="attributes"
              v-on="events"
            >
          </template>
        </v-select>
        <div
          v-if="v$.videos.$error"
          id="videosError"
          class="text-danger"
        >
          {{ $t(`validations.${v$.videos.$errors[0].$validator}`, { property: `${$t("forms.domain.hardware.videos")}` }) }}
        </div>
      </b-col>
      <!-- Boot -->
      <b-col
        cols="12"
        xl="2"
        class="mb-2"
      >
        {{ $t(`forms.domain.hardware.boot`) }}
        <v-select
          v-model="bootOrder"
          :options="availableHardware.bootOrder"
          label="name"
          :reduce="element => element.id"
          @search:blur="v$.bootOrder.$touch"
        >
          <template #search="{ attributes, events }">
            <input
              id="bootOrder"
              class="vs__search"
              v-bind="attributes"
              v-on="events"
            >
          </template>
        </v-select>
        <div
          v-if="v$.bootOrder.$error"
          id="bootOrderError"
          class="text-danger"
        >
          {{ $t(`validations.${v$.bootOrder.$errors[0].$validator}`, { property: `${$t("forms.domain.hardware.boot")}` }) }}
        </div>
      </b-col>
      <!-- Disk Bus -->
      <b-col
        cols="12"
        xl="2"
        class="mb-2"
      >
        {{ $t(`forms.domain.hardware.disk-bus`) }}
        <v-select
          v-model="diskBus"
          :options="availableHardware.diskBus"
          label="name"
          :reduce="element => element.id"
          @search:blur="v$.diskBus.$touch"
        >
          <template #search="{ attributes, events }">
            <input
              id="diskBus"
              class="vs__search"
              v-bind="attributes"
              v-on="events"
            >
          </template>
        </v-select>
        <div
          v-if="v$.diskBus.$error"
          id="diskBusError"
          class="text-danger"
        >
          {{ $t(`validations.${v$.diskBus.$errors[0].$validator}`, { property: `${$t("forms.domain.hardware.disk-bus")}` }) }}
        </div>
      </b-col>
      <!-- Disk Size -->
      <b-col
        v-if="showDiskSize"
        cols="12"
        xl="2"
        class="mb-2"
      >
        {{ $t(`forms.domain.hardware.disk-size`) }}
        <v-select
          v-model="diskSize"
          :options="availableHardware.diskSize"
          label="name"
          @search:blur="v$.diskSize.$touch"
        >
          <template #search="{ attributes, events }">
            <input
              id="diskSize"
              class="vs__search"
              v-bind="attributes"
              v-on="events"
            >
          </template>
        </v-select>
        <div
          v-if="v$.diskSize.$error"
          id="diskSizeError"
          class="text-danger"
        >
          {{ $t(`validations.${v$.diskSize.$errors[0].$validator}`, { property: `${$t("forms.domain.hardware.disk-size")}` }) }}
        </div>
      </b-col>
      <!-- Interfaces -->
      <b-col
        cols="12"
        xl="12"
        class="mb-2"
      >
        <div class="d-flex align-items-center mb-1">
          <span>{{ $t(`forms.domain.hardware.interfaces`) }}</span>
          <b-button
            variant="success"
            size="sm"
            class="ml-2 py-0 px-1"
            :title="$t('forms.domain.hardware.create-network')"
            @click="showCreateNetworkModal = true"
          >
            <b-icon icon="plus" /> {{ $t('forms.domain.hardware.new-network') }}
          </b-button>
        </div>
        <v-select
          v-model="interfaces"
          :options="groupedInterfaces"
          label="name"
          :close-on-select="false"
          :multiple="true"
          :reduce="element => element.id"
        >
          <template #option="option">
            <b-icon
              :icon="getInterfaceIcon(option)"
              :variant="getInterfaceVariant(option)"
              class="mr-1"
            />
            {{ option.name }}
            <small
              v-if="option.description"
              class="text-muted ml-1"
            >- {{ option.description }}</small>
          </template>
          <template #selected-option="option">
            <b-icon
              :icon="getInterfaceIcon(option)"
              :variant="getInterfaceVariant(option)"
              class="mr-1"
            />
            {{ option.name }}
          </template>
          <template #search="{ attributes, events }">
            <input
              id="interfaces"
              class="vs__search"
              v-bind="attributes"
              v-on="events"
            >
          </template>
        </v-select>
        <span v-if="domain.kind === 'desktop'">
          <template
            v-for="(network, index) in interfaces"
          >
            <template v-if="index > 0">, </template>{{ `${availableHardware.interfaces.find(e => e.id === interfaces[index]).name} - ${interfacesMac[index] ? interfacesMac[index] : `${$t("validations.undefined")}` }` }}
          </template>
        </span>
      </b-col>
      <!-- Internet Access -->
      <b-col
        cols="12"
        xl="2"
        class="mb-2"
      >
        <div class="d-flex align-items-center">
          <b-form-checkbox
            v-model="internet"
            switch
          >
            <b-icon
              :icon="internet ? 'wifi' : 'wifi-off'"
              :variant="internet ? 'success' : 'danger'"
              class="mr-1"
            />
            {{ internet ? $t('forms.domain.hardware.internet-enabled') : $t('forms.domain.hardware.internet-disabled') }}
          </b-form-checkbox>
        </div>
      </b-col>

      <!-- Create Network Modal -->
      <b-modal
        v-model="showCreateNetworkModal"
        :title="$t('forms.domain.hardware.create-network')"
        :ok-title="$t('forms.domain.hardware.create')"
        :cancel-title="$t('forms.cancel')"
        :ok-disabled="!newNetworkName.trim()"
        size="lg"
        @ok="createNetwork"
        @hidden="resetNetworkForm"
      >
        <b-form-group
          :label="$t('forms.domain.hardware.network-name')"
          label-for="new-network-name"
        >
          <b-form-input
            id="new-network-name"
            v-model="newNetworkName"
            :placeholder="$t('forms.domain.hardware.network-name-placeholder')"
            autofocus
          />
        </b-form-group>
        <b-form-group
          :label="$t('forms.domain.hardware.network-description')"
          label-for="new-network-description"
        >
          <b-form-input
            id="new-network-description"
            v-model="newNetworkDescription"
            :placeholder="$t('forms.domain.hardware.network-description-placeholder')"
          />
        </b-form-group>
        <b-form-group
          :label="$t('forms.domain.hardware.network-model')"
          label-for="new-network-model"
        >
          <b-form-select
            id="new-network-model"
            v-model="newNetworkModel"
            :options="networkModelOptions"
          />
          <small class="text-muted">{{ $t('forms.domain.hardware.network-model-help') }}</small>
        </b-form-group>
        <!-- Sharing section - only for advanced/manager/admin -->
        <div v-if="canShareNetworks">
          <NetworkAllowedForm
            :show-deployments="true"
            @update:allowed="newNetworkAllowed = $event"
          />
        </div>
      </b-modal>
    </b-row>
  </div>
</template>

<script>
import { computed, onMounted, watch, ref } from '@vue/composition-api'
import { hardwareWarningTitle, apiV3Segment } from '@/shared/constants'
import useVuelidate from '@vuelidate/core'
import { required, requiredIf } from '@vuelidate/validators'
import axios from 'axios'
import i18n from '@/i18n'
import NetworkAllowedForm from '@/components/NetworkAllowedForm.vue'

export default {
  components: {
    NetworkAllowedForm
  },
  props: {
    showDiskSize: {
      required: false,
      type: Boolean,
      default: false
    }
  },
  setup (props, context) {
    const $store = context.root.$store
    const $snotify = context.root.$snotify
    const domain = computed(() => $store.getters.getDomain)
    onMounted(() => {
      $store.dispatch('fetchHardware')
    })
    const availableHardware = computed(() => $store.getters.getHardware)

    // Network creation modal state
    const showCreateNetworkModal = ref(false)
    const newNetworkName = ref('')
    const newNetworkDescription = ref('')
    const newNetworkModel = ref('virtio')
    const newNetworkAllowed = ref({ groups: false, users: false, deployments: false })

    const networkModelOptions = computed(() => [
      { value: 'virtio', text: i18n.t('forms.domain.hardware.model-virtio') },
      { value: 'e1000', text: i18n.t('forms.domain.hardware.model-e1000') },
      { value: 'rtl8139', text: i18n.t('forms.domain.hardware.model-rtl8139') }
    ])

    // Check if user can share networks (not regular user role)
    const canShareNetworks = computed(() => {
      const user = $store.getters.getUser
      return user && ['advanced', 'manager', 'admin'].includes(user.role_id)
    })

    // Group interfaces by source for visual differentiation
    // Also include any selected interfaces that aren't in available list (e.g., reference networks)
    const groupedInterfaces = computed(() => {
      if (!availableHardware.value || !availableHardware.value.interfaces) {
        return []
      }
      const availableIds = new Set(availableHardware.value.interfaces.map(i => i.id))
      const systemInterfaces = availableHardware.value.interfaces
        .filter(i => i.source !== 'user_network' && i.source !== 'reference')
        .sort((a, b) => a.name.localeCompare(b.name))
      const userNetworks = availableHardware.value.interfaces
        .filter(i => i.source === 'user_network')
        .sort((a, b) => a.name.localeCompare(b.name))
      const referenceNetworks = availableHardware.value.interfaces
        .filter(i => i.source === 'reference')
        .sort((a, b) => a.name.localeCompare(b.name))

      // Add any selected interfaces that aren't in the available list
      // This handles cases where interface was removed from allowed but is still selected
      const selectedIds = domain.value?.hardware?.interfaces || []
      const missingInterfaces = selectedIds
        .filter(id => !availableIds.has(id))
        .map(id => ({
          id: id,
          name: id, // Use ID as name for unknown interfaces
          description: i18n.t('forms.domain.hardware.unknown-interface'),
          source: 'unknown'
        }))

      return [...systemInterfaces, ...userNetworks, ...referenceNetworks, ...missingInterfaces]
    })

    // Create a new user network
    const createNetwork = async (bvModalEvent) => {
      bvModalEvent.preventDefault()
      if (!newNetworkName.value.trim()) return

      try {
        const payload = {
          name: newNetworkName.value.trim(),
          description: newNetworkDescription.value.trim() || i18n.t('forms.domain.hardware.network-default-description'),
          model: newNetworkModel.value
        }

        // Add allowed if user has permission and any sharing is enabled
        if (canShareNetworks.value) {
          const allowed = newNetworkAllowed.value
          const hasSharing = allowed.groups !== false || allowed.users !== false || allowed.deployments !== false
          if (hasSharing) {
            payload.allowed = {
              roles: false,
              categories: false,
              groups: allowed.groups,
              users: allowed.users,
              deployments: allowed.deployments
            }
          }
        }

        const response = await axios.post(`${apiV3Segment}/user/networks`, payload)
        // Refresh hardware options to include new network
        await $store.dispatch('fetchHardware')
        // Auto-select the newly created network
        const newNetworkId = response.data.id
        domain.value.hardware.interfaces = [...domain.value.hardware.interfaces, newNetworkId]
        $store.commit('setDomain', domain.value)
        // Show success message
        $snotify.success(i18n.t('messages.info.network-created'))
        // Close modal
        showCreateNetworkModal.value = false
        resetNetworkForm()
      } catch (e) {
        const errorMsg = e.response?.data?.description || i18n.t('errors.network-creation-failed')
        $snotify.error(errorMsg)
      }
    }

    // Reset the network creation form
    const resetNetworkForm = () => {
      newNetworkName.value = ''
      newNetworkDescription.value = ''
      newNetworkModel.value = 'virtio'
      newNetworkAllowed.value = { groups: false, users: false, deployments: false }
    }
    const vcpus = computed({
      get: () => $store.getters.getDomain.hardware.vcpus,
      set: (value) => {
        domain.value.hardware.vcpus = value
        $store.commit('setDomain', domain.value)
      }
    })
    const memory = computed({
      get: () => $store.getters.getDomain.hardware.memory,
      set: (value) => {
        domain.value.hardware.memory = value
        $store.commit('setDomain', domain.value)
      }
    })
    const diskSize = computed({
      get: () => $store.getters.getDomain.hardware.diskSize,
      set: (value) => {
        domain.value.hardware.diskSize = value
        $store.commit('setDomain', domain.value)
      }
    })
    const graphics = computed({
      get: () => $store.getters.getDomain.hardware.graphics,
      set: (value) => {
        domain.value.hardware.graphics = value ? [value] : []
        $store.commit('setDomain', domain.value)
      }
    })
    const videos = computed({
      get: () => $store.getters.getDomain.hardware.videos,
      set: (value) => {
        domain.value.hardware.videos = value ? [value] : []
        $store.commit('setDomain', domain.value)
      }
    })
    const bootOrder = computed({
      get: () => $store.getters.getDomain.hardware.bootOrder,
      set: (value) => {
        domain.value.hardware.bootOrder = value ? [value] : []
        $store.commit('setDomain', domain.value)
      }
    })
    const diskBus = computed({
      get: () => $store.getters.getDomain.hardware.diskBus,
      set: (value) => {
        domain.value.hardware.diskBus = value
        $store.commit('setDomain', domain.value)
      }
    })
    const interfaces = computed({
      get: () => $store.getters.getDomain.hardware.interfaces,
      set: (value) => {
        domain.value.hardware.interfaces = value
        $store.commit('setDomain', domain.value)
      }
    })

    const internet = computed({
      get: () => $store.getters.getDomain.hardware.internet ?? true,
      set: (value) => {
        domain.value.hardware.internet = value
        $store.commit('setDomain', domain.value)
      }
    })

    const interfacesMac = computed(() => $store.getters.getDomain.hardware.interfacesMac)

    // When creating a desktop from a media if the user has the iso boot option it will be selected by default
    watch(availableHardware, (availableHardware, prevVal) => {
      if (props.showDiskSize && availableHardware.bootOrder.filter(boot =>
        boot.id === 'iso'
      ).length > 0) {
        bootOrder.value = 'iso'
      }
    })

    // Helper functions for interface icons
    // source: 'user_network', 'reference', 'deployment_shared', 'unknown', or default (admin/interface)
    // For user_network: is_owner + is_shared determine the icon
    // For reference: mode determines the icon
    const getInterfaceIcon = (option) => {
      const source = option.source
      const mode = option.mode || 'per-user'
      const isOwner = option.is_owner !== false // default true for backwards compat
      const isShared = option.is_shared === true
      const isDeploymentRestricted = option.is_deployment_restricted === true

      if (source === 'reference') {
        return mode === 'deployment-shared' ? 'link-45deg' : 'lock'
      }
      if (source === 'user_network') {
        // Deployment-restricted networks get a special icon
        if (isDeploymentRestricted) {
          return 'diagram-3-fill'
        }
        if (!isOwner) {
          // Shared with me by another user
          return 'people-fill'
        }
        // My own network
        return isShared ? 'unlock-fill' : 'lock-fill'
      }
      switch (source) {
        case 'deployment_shared': return 'globe2'
        case 'unknown': return 'question-circle'
        default: return 'globe' // admin/interface
      }
    }

    const getInterfaceVariant = (option) => {
      const source = option.source
      const mode = option.mode || 'per-user'
      const isOwner = option.is_owner !== false
      const isShared = option.is_shared === true
      const isDeploymentRestricted = option.is_deployment_restricted === true

      if (source === 'reference') {
        return mode === 'deployment-shared' ? 'warning' : 'info'
      }
      if (source === 'user_network') {
        // Deployment-restricted networks get purple variant
        if (isDeploymentRestricted) {
          return 'primary'
        }
        if (!isOwner) {
          // Shared with me
          return 'success'
        }
        // My own network
        return isShared ? 'warning' : 'info'
      }
      switch (source) {
        case 'deployment_shared': return 'success'
        case 'unknown': return 'danger'
        default: return 'secondary'
      }
    }

    return {
      vcpus,
      memory,
      diskSize,
      graphics,
      videos,
      bootOrder,
      diskBus,
      interfaces,
      internet,
      interfacesMac,
      availableHardware,
      domain,
      hardwareWarningTitle,
      // Network creation
      groupedInterfaces,
      showCreateNetworkModal,
      newNetworkName,
      newNetworkDescription,
      newNetworkModel,
      newNetworkAllowed,
      networkModelOptions,
      canShareNetworks,
      createNetwork,
      resetNetworkForm,
      // Interface helpers
      getInterfaceIcon,
      getInterfaceVariant,
      v$: useVuelidate({
        vcpus: {
          required
        },
        memory: {
          required
        },
        graphics: {
          required
        },
        videos: {
          required
        },
        bootOrder: {
          required
        },
        diskBus: {
          required
        },
        diskSize: {
          required: requiredIf(function () {
            return !!props.showDiskSize
          })
        }
      }, {
        vcpus,
        memory,
        graphics,
        videos,
        bootOrder,
        diskBus,
        diskSize
      })
    }
  }
}
</script>
